<div class="main-content pick-board-shell">
<div class="pick-board" #boardRoot [class.is-fullscreen]="fullscreen" [class.theme-light]="isLight">

	<!-- ── Marquee: who we are, what time it is, what's next out the door ── -->
	<header class="board-top">
		<div class="board-id">
			<div class="board-title">PICK BOARD</div>
			<div class="board-loc">{{ boardTitle }}</div>
			<div class="board-scope" *ngIf="config.name && locationName">{{ locationName }}</div>
			<!-- With several sites in scope the header name is a count, so spell them
			     out — nobody should have to guess which warehouses a wall covers. -->
			<div class="board-scope sites" *ngIf="multiLocation && scopeLabel">{{ scopeLabel }}</div>
		</div>

		<div class="board-clock">
			<div class="clock-time">{{ now | date:'h:mm' }}<span class="secs">:{{ now | date:'ss' }}</span><span class="ampm">{{ now | date:'a' }}</span></div>
			<div class="clock-date">{{ now | date:'EEEE, MMM d' }}</div>
		</div>

		<div class="board-cutoffs">
			<div class="cutoff" *ngFor="let c of cutoffs" [ngClass]="cutoffTone(c)"
				[class.example]="c.is_default">
				<div class="cut-label">{{ c.label }}</div>
				<div class="cut-count">{{ countdown(c) }}</div>
				<div class="cut-meta">
					<span class="cut-time">{{ c.at ? (c.at | date:'h:mm a') : c.time }}</span>
					<span class="cut-load" *ngIf="c.orders">· {{ c.orders }} open</span>
					<span class="cut-eg" *ngIf="c.is_default">example</span>
				</div>
			</div>
			<button type="button" class="cutoff ok set-cut" *ngIf="cutoffsAreDefault"
				(click)="openSettings($event)" title="Set the real cutoff times">
				<div class="cut-label">Not set yet</div>
				<div class="cut-count"><i class="material-icons">schedule</i></div>
				<div class="cut-meta"><span class="cut-time">Set times</span></div>
			</button>
			<button type="button" class="cutoff ok set-cut" *ngIf="!cutoffs.length"
				(click)="openSettings($event)" title="Set the cutoff times">
				<div class="cut-label">No cutoffs set</div>
				<div class="cut-count"><i class="material-icons">schedule</i></div>
			</button>
		</div>

		<div class="board-chrome">
			<span class="live-dot" [class.on]="live" [title]="live ? 'Live — pushed from the floor' : 'No live feed — polling every 8s'"></span>
			<span class="updated" *ngIf="lastUpdated">{{ lastUpdated | date:'h:mm:ss a' }}</span>
			<button type="button" class="chrome-btn" (click)="load()" aria-label="Refresh board">
				<i class="material-icons">refresh</i>
			</button>
			<button type="button" class="chrome-btn" (click)="toggleTheme($event)"
				[attr.aria-label]="isLight ? 'Switch to dark' : 'Switch to light'"
				[title]="isLight ? 'Switch to dark' : 'Switch to light'">
				<i class="material-icons">{{ isLight ? 'brightness_2' : 'wb_sunny' }}</i>
			</button>
			<button type="button" class="chrome-btn" (click)="openSettings($event)" aria-label="Board settings">
				<i class="material-icons">settings</i>
			</button>
			<button type="button" class="chrome-btn" (click)="toggleFullscreen()" aria-label="Toggle fullscreen">
				<i class="material-icons">{{ fullscreen ? 'fullscreen_exit' : 'fullscreen' }}</i>
			</button>
		</div>
	</header>

	<!-- What this screen is showing. Always stated: an unlabelled filtered board
	     reads as "the warehouse is quiet" when it is anything but. -->
	<section class="board-filters" *ngIf="isFiltered">
		<i class="material-icons">filter_list</i>
		<span class="bf-label">Showing</span>
		<span class="bf-chip" *ngFor="let f of filterSummary">{{ f }}</span>
		<span class="bf-stage" *ngIf="config.stage === 'check'">CHECKING</span>
	</section>

	<!-- ── Floor at a glance ── -->
	<section class="board-stats">
		<div class="stat">
			<div class="stat-val">{{ stats.to_pick }}</div>
			<div class="stat-lbl">To Pick</div>
		</div>
		<div class="stat is-progress">
			<div class="stat-val">{{ stats.in_progress }}</div>
			<div class="stat-lbl">In Progress</div>
		</div>
		<div class="stat is-ready">
			<div class="stat-val">{{ stats.ready }}</div>
			<div class="stat-lbl">Ready</div>
		</div>
		<div class="stat is-late" [class.zero]="!stats.late">
			<div class="stat-val">{{ stats.late }}</div>
			<div class="stat-lbl">Late</div>
		</div>
		<div class="stat is-short" [class.zero]="!stats.short">
			<div class="stat-val">{{ stats.short }}</div>
			<div class="stat-lbl">Short</div>
		</div>
		<div class="stat">
			<div class="stat-val">{{ stats.lines_done }}<span class="of">/{{ stats.lines }}</span></div>
			<div class="stat-lbl">Lines</div>
		</div>
		<div class="stat">
			<div class="stat-val">{{ stats.units_picked | number:'1.0-0' }}<span class="of">/{{ stats.units | number:'1.0-0' }}</span></div>
			<div class="stat-lbl">Units</div>
		</div>
		<div class="stat is-people">
			<div class="stat-val">{{ stats.pickers }}</div>
			<div class="stat-lbl">Pickers</div>
		</div>
	</section>

	<div class="board-error" *ngIf="error">
		<i class="material-icons">error</i> {{ error }}
	</div>

	<div class="board-loading" *ngIf="loading && !orders.length && !error">
		<mat-progress-spinner mode="indeterminate" diameter="48" color="accent"></mat-progress-spinner>
	</div>

	<!-- ── The queue. Rank 1 is the next order off the floor. ── -->
	<section class="board-body" *ngIf="!loading || orders.length">
		<div class="queue">
			<div class="queue-head">
				<span class="qh-title">Pick Next</span>
				<span class="qh-page" *ngIf="pageCount > 1">{{ page + 1 }} / {{ pageCount }}</span>
			</div>

			<article class="qrow" *ngFor="let o of pagedOrders; let i = index; trackBy: trackByOrderno"
				[ngClass]="rowTone(o)" (click)="openOrder(o)">
				<div class="rank">{{ rankOf(i) }}</div>

				<div class="qmain">
					<div class="qline1">
						<span class="ordno">#{{ o.orderno }}</span>
						<span class="cust">{{ o.customer }}</span>
						<span class="flag late" *ngIf="o.late">LATE</span>
						<span class="flag asap" *ngIf="o.asap && !o.late">ASAP</span>
						<span class="flag short" *ngIf="o.short_lines > 0">SHORT {{ o.short_lines }}</span>
						<span class="flag picker" *ngIf="o.picker" [class.idle]="!o.picker_active">
							<i class="material-icons">person</i>{{ o.picker }}
						</span>
					</div>
					<div class="qline2">
						<span class="chip site" *ngIf="showRowLocation && o.location_name">{{ o.location_name }}</span>
						<span class="chip" *ngIf="o.city">{{ o.city }}</span>
						<span class="chip" *ngIf="o.shipvia">{{ o.shipvia }}</span>
						<span class="chip bins" *ngIf="o.bin_range">
							<i class="material-icons">grid_view</i>{{ o.bin_range }}
						</span>
						<span class="chip">{{ o.line_count }} {{ o.line_count === 1 ? 'line' : 'lines' }}</span>
						<span class="chip">{{ o.qty_pick | number:'1.0-0' }} units</span>
						<span class="chip age">{{ age(o) }} old</span>
					</div>
				</div>

				<div class="qprog">
					<div class="prog-nums">{{ o.lines_done }}<span class="of">/{{ o.line_count }}</span></div>
					<div class="bar"><div class="fill" [style.width.%]="o.pct"></div></div>
				</div>

				<div class="qcut">
					<div class="cut-val">{{ orderCountdown(o) }}</div>
					<div class="cut-lbl">{{ o.cutoff_label || 'no cutoff' }}</div>
				</div>
			</article>

			<!-- An empty FILTERED board is not an empty warehouse. Saying "floor is
			     clear" when a screen filter is hiding the work reads as "we're done"
			     and sends people home. Say which it is, and offer the way out. -->
			<div class="queue-empty" *ngIf="!orders.length && !error">
				<ng-container *ngIf="isFiltered; else trulyClear">
					<i class="material-icons">filter_list</i>
					<span>Nothing matches this screen's filter.</span>
					<span class="qe-sub">{{ filterSummary.join(' · ') }}</span>
					<button type="button" class="qe-btn" (click)="clearFilters()">Show everything</button>
				</ng-container>
				<ng-template #trulyClear>
					<i class="material-icons">check_circle</i>
					<span>Floor is clear — nothing waiting to be picked.</span>
				</ng-template>
			</div>
		</div>

		<!-- Staged work: picked, waiting on load-out. A section screen can turn this
		     off — it isn't that screen's business. -->
		<aside class="rail" *ngIf="config.showRail !== false">
			<div class="rail-head">
				<span>Picked &amp; Staged</span>
				<span class="rail-count">{{ (truncated?.ready_total ?? ready.length) | number }}</span>
			</div>
			<div class="rail-list">
				<div class="rrow" *ngFor="let o of railOrders; trackBy: trackByOrderno"
					[class.short-close]="o.closed_short" (click)="openOrder(o)">
					<span class="rord">#{{ o.orderno }}</span>
					<span class="rcust">{{ o.customer }}</span>
					<!-- Signed off with less counted than the order asked for. On the wall
					     that needs saying, not a quietly short progress bar. -->
					<span class="rshort" *ngIf="o.closed_short"
						[title]="o.qty_picked + ' of ' + o.qty_pick + ' counted'">SHORT</span>
					<span class="rqty">{{ o.qty_picked | number:'1.0-0' }}</span>
				</div>
				<div class="rail-empty" *ngIf="!ready.length">Nothing staged yet.</div>
				<div class="rail-more" *ngIf="railHidden">+ {{ railHidden | number }} more staged</div>
			</div>

			<!-- Truncation is stated, never silent: a board that quietly drops work
			     reads as "all clear" when it is anything but. -->
			<div class="rail-note is-cap" *ngIf="queueHidden">
				<i class="material-icons">warning</i>
				{{ queueHidden | number }} more waiting — list capped at {{ truncated?.cap }}
			</div>
			<div class="rail-note" *ngIf="stats.held">
				<i class="material-icons">block</i>
				{{ stats.held }} order{{ stats.held === 1 ? '' : 's' }} on hold — not pickable
			</div>
			<!-- Residue in pickingfreeze: closed or gone with nothing ever picked.
			     Not work, but saying the number is how anyone finds out the table
			     needs clearing. -->
			<div class="rail-note is-stale" *ngIf="stats.stale">
				<i class="material-icons">layers</i>
				{{ stats.stale | number }} stale row{{ stats.stale === 1 ? '' : 's' }} ignored
			</div>
		</aside>
	</section>


	<!-- ── Settings: set once per screen, kept in this browser ── -->
	<!-- No close-on-backdrop: a stray tap used to discard every change with no
	     warning, which on a TV is very easy to do. Cancel and Save are explicit. -->
	<div class="board-settings" *ngIf="settingsOpen">
		<div class="bs-panel" (click)="$event.stopPropagation()">
			<header class="bs-head">
				<i class="material-icons">settings</i>
				<span class="bs-title">This screen</span>
				<button type="button" class="bs-close" (click)="closeSettings()" aria-label="Close settings">
					<i class="material-icons">close</i>
				</button>
			</header>

			<div class="bs-body">
				<label class="bs-field wide">
					<span class="bs-k">Screen name</span>
					<input type="text" [(ngModel)]="config.name" placeholder="e.g. Parcel Lane, Aisle B, Checking" />
				</label>

				<div class="bs-row">
					<label class="bs-field">
						<span class="bs-k">Stage</span>
						<select [(ngModel)]="config.stage">
							<option value="pick">Picking</option>
							<option value="check">Checking</option>
						</select>
					</label>
					<label class="bs-field">
						<span class="bs-k">Rows / page</span>
						<input type="number" min="3" max="20" [(ngModel)]="config.pageSize" />
					</label>
					<label class="bs-field">
						<span class="bs-k">Rotate (sec)</span>
						<input type="number" min="4" max="120" [(ngModel)]="config.rotateSec" />
					</label>
					<label class="bs-field">
						<span class="bs-k">Theme</span>
						<select [(ngModel)]="config.theme">
							<option value="dark">Dark — dim warehouse</option>
							<option value="light">Light — bright / window</option>
						</select>
					</label>
					<label class="bs-field bs-check">
						<input type="checkbox" [(ngModel)]="config.showRail" />
						<span>Show "Picked &amp; Staged" column</span>
					</label>
				</div>

				<!-- What's set right now, each removable, plus how many orders it
				     matches — so a screen is never saved pointing at nothing. -->
				<div class="bs-active">
					<div class="bs-active-head">
						<span class="bs-k">This screen shows</span>
						<span class="bs-match" [class.none]="previewMatches === 0">
							<ng-container *ngIf="previewing">counting…</ng-container>
							<ng-container *ngIf="!previewing && previewMatches !== null">
								{{ previewMatches | number }} order{{ previewMatches === 1 ? '' : 's' }} right now
							</ng-container>
						</span>
					</div>
					<div class="bs-active-chips">
						<span class="bs-none" *ngIf="!hasPendingFilters">Everything in this location</span>
						<button type="button" class="bs-sel" *ngFor="let f of selectedFilters"
							(click)="removeSelected(f)" [attr.aria-label]="'Remove ' + f.label + ' ' + f.shown">
							<span class="bs-sel-k">{{ f.label }}</span>{{ f.shown }}
							<i class="material-icons">close</i>
						</button>
						<span class="bs-sel typed" *ngIf="config.binPattern">Bin pattern {{ config.binPattern }}</span>
						<span class="bs-sel typed" *ngIf="config.zip">Zip {{ config.zip }}</span>
						<span class="bs-sel typed" *ngIf="config.line">Line {{ config.line }}</span>
					</div>
					<div class="bs-warn" *ngIf="!previewing && previewMatches === 0">
						<i class="material-icons">warning</i>
						Nothing matches this right now — fine if the lane is just empty, worth a
						second look if you expected work.
					</div>
				</div>

				<div class="bs-loading" *ngIf="optionsLoading">Reading what's on the floor…</div>

				<div class="bs-group" *ngIf="options.locations.length > 1">
					<span class="bs-k">Warehouses <em class="bs-hint">pick any number — none = all</em></span>
					<div class="bs-chips scroll">
						<button type="button" class="bs-chip" [class.on]="!config.loccodes.length"
							(click)="config.loccodes = []; refreshPreview()">All warehouses</button>
						<button type="button" class="bs-chip" *ngFor="let l of options.locations"
							[class.on]="isFilterOn('loccodes', l.code)" (click)="toggleFilter('loccodes', l.code)">
							{{ l.name }} <em>{{ l.orders }}</em>
						</button>
					</div>
				</div>

				<div class="bs-group" *ngIf="options.shipvia.length">
					<span class="bs-k">Ship via</span>
					<div class="bs-chips">
						<button type="button" class="bs-chip" *ngFor="let v of options.shipvia"
							[class.on]="isFilterOn('shipvia', v.id)" (click)="toggleFilter('shipvia', v.id)">
							{{ v.name }} <em>{{ v.orders }}</em>
						</button>
					</div>
				</div>

				<div class="bs-group" *ngIf="binRooms.length">
					<span class="bs-k">Room <em class="bs-hint">whole letter</em></span>
					<div class="bs-chips">
						<button type="button" class="bs-chip" *ngFor="let r of binRooms"
							[class.on]="isRoomOn(r)" (click)="toggleRoom(r)">{{ r }}*</button>
					</div>
				</div>

				<div class="bs-group" *ngIf="options.bins.length">
					<span class="bs-k">Bins <em class="bs-hint">exact</em></span>
					<div class="bs-chips scroll">
						<!-- The bin's own description, shown not hovered: this panel gets
						     driven on a TV, where there is no hover. -->
						<button type="button" class="bs-chip stacked" *ngFor="let b of options.bins"
							[class.on]="isFilterOn('bins', b.name)" (click)="toggleFilter('bins', b.name)">
							<span class="bs-chip-main">{{ b.name }} <em>{{ b.lines_open }}</em></span>
							<span class="bs-chip-sub" *ngIf="b.description">{{ b.description }}</span>
						</button>
					</div>
				</div>

				<div class="bs-group" *ngIf="options.areas.length">
					<span class="bs-k">Area</span>
					<div class="bs-chips scroll">
						<button type="button" class="bs-chip stacked" *ngFor="let a of options.areas"
							[class.on]="isFilterOn('area', a.code)" (click)="toggleFilter('area', a.code)">
							<span class="bs-chip-main">{{ a.name }} <em>{{ a.orders }}</em></span>
							<span class="bs-chip-sub">code {{ a.code }} · {{ a.branches | number }} customers</span>
						</button>
					</div>
				</div>

				<div class="bs-group" *ngIf="options.routes.length">
					<span class="bs-k">Route</span>
					<div class="bs-chips scroll">
						<button type="button" class="bs-chip stacked" *ngFor="let r of options.routes"
							[class.on]="isFilterOn('deliveryroute', r.code)" (click)="toggleFilter('deliveryroute', r.code)">
							<span class="bs-chip-main">{{ r.name }} <em>{{ r.orders }}</em></span>
							<span class="bs-chip-sub">route {{ r.code }} · {{ r.branches | number }} customers</span>
						</button>
					</div>
				</div>

				<div class="bs-row">
					<label class="bs-field wide">
						<span class="bs-k">Bin pattern <em class="bs-hint">RM*, B% — covers a room without ticking each bin</em></span>
						<input type="text" [(ngModel)]="config.binPattern"
							(ngModelChange)="refreshPreview()" placeholder="RM*, B%" />
					</label>
					<label class="bs-field wide">
						<span class="bs-k">Zip <em class="bs-hint">comma list, * for a range</em></span>
						<input type="text" [(ngModel)]="config.zip" (ngModelChange)="refreshPreview()" placeholder="142*, 14221" />
					</label>
					<label class="bs-field wide">
						<span class="bs-k">Product line <em class="bs-hint">comma list, * for prefix</em></span>
						<input type="text" [(ngModel)]="config.line" (ngModelChange)="refreshPreview()" placeholder="AMI*, OLA" />
					</label>
				</div>

				<div class="bs-shared">
					<div class="bs-shared-head">
						<i class="material-icons">schedule</i>
						<span class="bs-k">Cutoff times</span>
						<em class="bs-shared-tag">every board, not just this screen</em>
					</div>

					<p class="bs-shared-note" *ngIf="cutoffsAreDefault">
						Nobody has set these — the wall is showing examples. Put your real
						times in and save to make them count.
					</p>

					<div class="bs-cut" *ngFor="let c of cutoffEdit; let i = index">
						<div class="bs-cut-main">
							<input type="text" class="cut-name" [(ngModel)]="c.label" maxlength="40"
								(ngModelChange)="cutoffTouched()"
								placeholder="What the box says — UPS Pickup, Local Run" />
							<input type="time" class="cut-when" [(ngModel)]="c.time" (ngModelChange)="cutoffTouched()" />
							<select class="cut-loc" [(ngModel)]="c.loccode" (ngModelChange)="cutoffTouched()"
								*ngIf="options.locations.length > 1">
								<option value="">All warehouses</option>
								<option *ngFor="let l of options.locations" [value]="l.code">{{ l.name }}</option>
							</select>
							<button type="button" class="bs-cut-del" (click)="removeCutoff(i)"
								[attr.aria-label]="'Remove ' + (c.label || 'cutoff')">
								<i class="material-icons">close</i>
							</button>
						</div>
						<div class="bs-cut-scope" *ngIf="options.shipvia.length">
							<span class="bs-cut-k">Counts</span>
							<button type="button" class="bs-chip sm" *ngFor="let v of options.shipvia"
								[class.on]="isCutoffShipvia(c, v.id)" (click)="toggleCutoffShipvia(c, v.id)">
								{{ v.name }}
							</button>
							<em class="bs-cut-all" *ngIf="!c.shipvia.length">everything not claimed above</em>
						</div>
					</div>

					<p class="bs-shared-note" *ngIf="!cutoffEdit.length">
						No cutoffs. The board will show no countdown — fine if nothing here
						runs to a deadline.
					</p>

					<div class="bs-shared-foot">
						<button type="button" class="bs-btn ghost" (click)="addCutoff()"
							[disabled]="cutoffEdit.length >= cutoffMax">
							Add cutoff
						</button>
						<span class="bs-cut-lim" *ngIf="cutoffEdit.length >= cutoffMax">
							{{ cutoffMax }} is all that stays readable across a room
						</span>
						<span class="bs-spacer"></span>
						<span class="bs-cut-msg err" *ngIf="cutoffError">{{ cutoffError }}</span>
						<span class="bs-cut-msg ok" *ngIf="cutoffSaved && !cutoffError">Saved for every board</span>
						<span class="bs-cut-msg warn" *ngIf="cutoffDirty && !cutoffError">
							Unsaved — the button below won't save these
						</span>
						<button type="button" class="bs-btn cut-save" [class.dirty]="cutoffDirty"
							(click)="saveCutoffs()" [disabled]="cutoffSaving">
							{{ cutoffSaving ? 'Saving…' : 'Save cutoffs' }}
						</button>
					</div>
				</div>
			</div>

			<footer class="bs-foot">
				<button type="button" class="bs-btn ghost" (click)="clearFilters()">Clear filters</button>
				<span class="bs-spacer"></span>
				<button type="button" class="bs-btn ghost" (click)="closeSettings()">Cancel</button>
				<button type="button" class="bs-btn go" (click)="applySettings()">Save to this screen</button>
			</footer>
		</div>
	</div>

</div>
</div>
